import React, { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  HStack,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import ReusableTable from "../../components/ReusableTable";
import ReusableFilter from "../../components/ReusableFilter";
import ReusableModal from "../../components/ReusableModal";
import { getUserVtuTransactions } from "./services";

const PAGE_SIZE = 10;

const getStatusColor = (status) => {
  if (status === "Successful") return "green";
  if (status === "Pending") return "yellow";
  return "red";
};

export const TransactionsHistoryView = () => {
  const {
    isOpen: isTransactionModalOpen,
    onOpen: openTransactionModal,
    onClose: closeTransactionModal,
  } = useDisclosure();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [serviceTypeFilter, setServiceTypeFilter] = useState("");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const queryParams = useMemo(
    () => ({
      page: currentPage,
      limit: PAGE_SIZE,
      search: searchValue || undefined,
      status: statusFilter || undefined,
      serviceType: serviceTypeFilter || undefined,
      startDate: startDateFilter || undefined,
      endDate: endDateFilter || undefined,
    }),
    [
      currentPage,
      searchValue,
      statusFilter,
      serviceTypeFilter,
      startDateFilter,
      endDateFilter,
    ],
  );

  const { data: transactionsResponse, isLoading: transactionsLoading } =
    useQuery({
      queryKey: ["vtu-transactions", queryParams],
      queryFn: () => getUserVtuTransactions(queryParams),
      keepPreviousData: true,
    });

  const paginatedData = useMemo(() => {
    const items = Array.isArray(transactionsResponse?.items)
      ? transactionsResponse.items
      : [];

    return items.map((transaction) => ({
      ...transaction,
      amount: Number(transaction.amount) || 0,
      date: transaction?.date
        ? new Date(transaction.date).toLocaleString()
        : "-",
    }));
  }, [transactionsResponse]);

  const pagination = transactionsResponse?.pagination || {};
  const totalPages = Math.max(1, Number(pagination.totalPages) || 1);

  const handleView = (transaction) => {
    setSelectedTransaction(transaction);
    openTransactionModal();
  };

  const handleSearchChange = (value) => {
    setCurrentPage(1);
    setSearchValue(value);
  };

  const handleFieldChange = (key, value) => {
    setCurrentPage(1);

    if (key === "status") setStatusFilter(value);
    if (key === "serviceType") setServiceTypeFilter(value);
    if (key === "startDate") setStartDateFilter(value);
    if (key === "endDate") setEndDateFilter(value);
  };

  const handleResetFilters = () => {
    setCurrentPage(1);
    setSearchValue("");
    setStatusFilter("");
    setServiceTypeFilter("");
    setStartDateFilter("");
    setEndDateFilter("");
  };

  const filterFields = [
    {
      key: "status",
      label: "Status",
      type: "select",
      value: statusFilter,
      options: [
        { label: "Successful", value: "CONFIRMED" },
        { label: "Pending", value: "PENDING" },
        { label: "Failed", value: "FAILED" },
      ],
    },
    {
      key: "serviceType",
      label: "Service Type",
      type: "select",
      value: serviceTypeFilter,
      options: [
        { label: "Airtime", value: "AIRTIME" },
        { label: "Data", value: "DATA" },
        { label: "Cable", value: "TV" },
        { label: "Electricity", value: "ELECTRICITY" },
      ],
    },
    {
      key: "startDate",
      label: "Start Date",
      type: "date",
      value: startDateFilter,
    },
    {
      key: "endDate",
      label: "End Date",
      type: "date",
      value: endDateFilter,
    },
  ];

  const columns = [
    {
      key: "transactionId",
      header: "Transaction Ref",
    },
    {
      key: "itemTitle",
      header: "Title",
    },
    {
      key: "phoneOrDecoderNumber",
      header: "Phone/Decoder Number",
    },
    {
      key: "amount",
      header: "Amount",
      isNumeric: true,
      render: (row) => `₦${row.amount.toLocaleString()}`,
    },
    {
      key: "status",
      header: "Status",
      render: (row) => (
        <Badge
          colorScheme={getStatusColor(row.status)}
          borderRadius="full"
          px="8px"
          py="2px"
          textTransform="none"
        >
          {row.status}
        </Badge>
      ),
    },
    {
      key: "date",
      header: "Date",
    },
    {
      key: "action",
      header: "Action",
      render: (row) => (
        <Button size="xs" onClick={() => handleView(row)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <Box
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="default"
      p="20px"
      bg="white"
    >
      <HStack justifyContent="space-between" mb="14px">
        <Text fontSize="20px" fontWeight="700" color="primary.500">
          Transactions History
        </Text>
      </HStack>

      <ReusableFilter
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        fields={filterFields}
        onFieldChange={handleFieldChange}
        onReset={handleResetFilters}
        searchPlaceholder="Search by transaction ref, title, number, status..."
      />

      <ReusableTable
        columns={columns}
        data={paginatedData}
        loading={transactionsLoading}
        enablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      <ReusableModal
        isOpen={isTransactionModalOpen}
        onClose={closeTransactionModal}
        title="Transaction Details"
        size="md"
        footer={
          <Button size="sm" onClick={closeTransactionModal}>
            Close
          </Button>
        }
      >
        {selectedTransaction ? (
          <VStack align="stretch" spacing="10px" fontSize="14px" pb="6px">
            <HStack justifyContent="space-between">
              <Text color="gray.600">Transaction Ref</Text>
              <Text fontWeight="600">{selectedTransaction.transactionId}</Text>
            </HStack>
            <HStack justifyContent="space-between">
              <Text color="gray.600">Item Title</Text>
              <Text fontWeight="600">{selectedTransaction.itemTitle}</Text>
            </HStack>
            <HStack justifyContent="space-between">
              <Text color="gray.600">Phone/Decoder Number</Text>
              <Text fontWeight="600">
                {selectedTransaction.phoneOrDecoderNumber}
              </Text>
            </HStack>
            <HStack justifyContent="space-between">
              <Text color="gray.600">Amount</Text>
              <Text fontWeight="600">
                ₦{(Number(selectedTransaction.amount) || 0).toLocaleString()}
              </Text>
            </HStack>
            <HStack justifyContent="space-between">
              <Text color="gray.600">Status</Text>
              <Badge
                colorScheme={getStatusColor(selectedTransaction.status)}
                borderRadius="full"
                px="8px"
                py="2px"
                textTransform="none"
              >
                {selectedTransaction.status}
              </Badge>
            </HStack>
            <HStack justifyContent="space-between">
              <Text color="gray.600">Date</Text>
              <Text fontWeight="600">{selectedTransaction.date}</Text>
            </HStack>
          </VStack>
        ) : null}
      </ReusableModal>
    </Box>
  );
};
